// Self-serve signup flow (signup.html).
//
// A 4-step stepper wired to the live Go backend. The stepper collects account +
// workspace + team/plan locally, then on "Create workspace" runs a strict async
// sequence: POST /signup -> (dev) POST /signup/verify -> invites -> first API key
// -> success. In prod the verification token isn't returned, so we render a
// "check your email" state and stop. The emailed verify link lands back here
// with ?tenant_id&token and jumps straight to verify + key issue + success.
import "../../shared/design-tokens/tokens.css";
import "./platform.css";
import { mountIcons } from "./icons.js";
import { brand, applyBrand } from "./brand.js";
import {
  apiBase,
  slugAvailable,
  signup,
  verifySignup,
  createInvite,
  resendSignup,
  createKey,
} from "./api.js";

const MIN_PASSWORD = 12;
const SLUG_DEBOUNCE_MS = 400;
const ADMIN_TOKEN_KEY = "iris.admin.token";
// Teammates + plan entered before the email hop are parked here (per reserved
// tenant) so the verify-link landing can still send the invites. Never holds
// passwords or tokens.
const PENDING_SETUP_KEY = "iris.signup.pending";
const ADMIN_EMAIL_KEY = "iris.admin.email";

// Provisional access profiles. `id` remains the string sent to POST /signup
// for backward compatibility; no public price is attached while D-0024 defers
// paid-production pricing.
const PLANS = [
  {
    id: "developer",
    name: "Developer",
    desc: "Small evaluation workspace",
    price: "Early access",
    per: "no live billing",
  },
  {
    id: "team",
    name: "Team",
    desc: `Team evaluation · ${brand.agent} replies`,
    price: "Early access",
    per: "no live billing",
  },
  {
    id: "business",
    name: "Business",
    desc: "Provisional larger-team profile",
    price: "Preview",
    per: "pricing deferred",
  },
];

const ROLES = ["Operator", "Admin", "Viewer"];

const state = {
  step: 0,
  // step 0 — account (held in memory only; password is never logged/persisted)
  name: "",
  email: "",
  password: "",
  // step 1 — workspace
  workspace: "",
  slug: "",
  slugStatus: "idle", // idle | checking | available | taken | invalid
  // step 2 — team & plan
  plan: "team",
  invites: [{ email: "", role: "Operator" }],
  // results / flow
  formError: "", // step-level error banner text
  busy: false, // submit in flight
  session: null, // verified <SESSION> (keeps SESSION.token bearer in memory)
  adminSessionReady: false, // same-origin Admin continuation token was stored
  resultSlug: "",
  resultTenantID: "",
  apiSecret: "", // once-shown publishable key secret
  keyPrefix: "",
  resendNotice: "", // result of the last "send the email again" click
  resendError: false,
  inviteWarning: "", // note if some invites failed (best-effort)
  invitesSent: [], // emails that were invited during setup (shown on the success screen)
  emailPending: false, // prod: must click emailed link
};

const panel = document.getElementById("panel");
const stepper = document.getElementById("stepper");
const topbar = document.getElementById("topbar");

const $ = (id) => document.getElementById(id);
const esc = (s) =>
  String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 28);
}

// A locally-valid slug: lowercase, digits, hyphens, 3-28 chars, no leading/
// trailing hyphen. The server is authoritative for availability; this just
// gates the obviously-bad inputs before we ask.
function slugLooksValid(slug) {
  return /^[a-z0-9](?:[a-z0-9-]{1,26}[a-z0-9])$/.test(slug);
}

// "24 hours" / "45 minutes" from an ISO expiry, for the check-your-email copy.
function humanExpiry(iso) {
  const ms = Date.parse(iso || "") - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const hours = Math.round(ms / 3600000);
  if (hours >= 2) return `${hours} hours`;
  const minutes = Math.max(1, Math.round(ms / 60000));
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function isEmail(v) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
}

// ---- stepper ---------------------------------------------------------------

function renderStepper() {
  [...stepper.children].forEach((el, i) => {
    el.classList.toggle("active", i === state.step);
    el.classList.toggle("done", i < state.step);
    const num = el.querySelector(".num");
    num.innerHTML = i < state.step ? '<i data-lucide="check"></i>' : i + 1;
  });
}

// ---- views -----------------------------------------------------------------

const views = {
  0: () => `
    <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Step 1 of 4</span>
    <h1>Create your account</h1>
    <p class="sub">Use your work email — it becomes the owner of this workspace.</p>
    ${formErrorHTML()}
    <label class="fld" for="f_name"><span class="cap">Full name</span><input class="inp" id="f_name" placeholder="Sam Ortiz" value="${esc(state.name)}" autocomplete="name"></label>
    <label class="fld" for="f_email"><span class="cap">Work email</span><input class="inp" id="f_email" type="email" placeholder="you@company.com" value="${esc(state.email)}" autocomplete="email"><span class="field-err" id="emailErr"></span></label>
    <label class="fld" for="f_pass"><span class="cap">Password</span><input class="inp" id="f_pass" type="password" placeholder="At least ${MIN_PASSWORD} characters" value="${esc(state.password)}" autocomplete="new-password"><span class="field-err" id="passErr"></span></label>
    <div class="nav"><div class="grow"></div><button class="pf-btn pf-btn--primary pf-btn--lg" id="next" type="button">Continue<i data-lucide="arrow-right"></i></button></div>`,

  1: () => `
    <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Step 2 of 4</span>
    <h1>Name your workspace</h1>
    <p class="sub">Usually your company or clinic name. Your workspace is the private home for all of your conversations.</p>
    ${formErrorHTML()}
    <label class="fld" for="f_ws"><span class="cap">Workspace name</span><input class="inp" id="f_ws" placeholder="Acme Health" value="${esc(state.workspace)}"></label>
    <label class="fld" for="f_slug"><span class="cap">Workspace URL</span>
      <div class="slugwrap" id="slugWrap"><input id="f_slug" placeholder="acme-health" value="${esc(state.slug)}" autocapitalize="off" autocomplete="off" spellcheck="false"><span class="suffix">.${esc(brand.domain)}</span></div>
      <span class="hint" id="slugHint"></span>
    </label>
    <div class="nav"><button class="pf-btn pf-btn--ghost pf-btn--lg" id="back" type="button">Back</button><div class="grow"></div><button class="pf-btn pf-btn--primary pf-btn--lg" id="next" type="button">Continue<i data-lucide="arrow-right"></i></button></div>`,

  2: () => `
    <span class="pf-eyebrow pf-eyebrow--teal"><span class="spark"></span>Step 3 of 4</span>
    <h1>Bring your team &amp; pick an access profile</h1>
    <p class="sub">Invite the operators who'll answer conversations. You can change this anytime in Admin.</p>
    ${formErrorHTML()}
    <div id="invites"></div>
    <button class="addmore" id="addInvite" type="button"><i data-lucide="plus"></i>Add another teammate</button>
    <div style="height:24px"></div>
    <div class="cap" style="font:700 11px/1 var(--uc-font-body);text-transform:uppercase;letter-spacing:.08em;color:var(--uc-fg2);margin-bottom:10px">Plan</div>
    <div class="plans" id="plans">
      ${PLANS.map(
        (p) => `<label class="plan ${state.plan === p.id ? "on" : ""}" data-plan="${p.id}"><span class="radio"></span><span class="pn"><b>${esc(p.name)}</b><span>${esc(p.desc)}</span></span><span class="pp">${esc(p.price)}<small>${esc(p.per)}</small></span></label>`,
      ).join("")}
    </div>
    <div class="nav"><button class="pf-btn pf-btn--ghost pf-btn--lg" id="back" type="button">Back</button><div class="grow"></div><button class="pf-btn pf-btn--primary pf-btn--lg" id="next" type="button">Create workspace<i data-lucide="arrow-right"></i></button></div>`,

  3: () => `
    <div class="done-ic"><i data-lucide="check"></i></div>
    <h1>Your workspace is live.</h1>
    <p class="sub"><b style="color:var(--uc-fg1)">${esc(state.workspace || state.resultSlug)}</b> is ready. Paste the snippet below, then use Admin for team, keys, billing, widget settings, and install details.</p>
    ${state.adminSessionReady ? `<div class="notice"><i data-lucide="shield-check"></i><span>Your owner session is ready for Admin in this browser. The API key secret is still only shown on this screen.</span></div>` : ""}
    ${state.invitesSent.length ? `<div class="notice"><i data-lucide="mail-check"></i><span>Invites sent to <b>${state.invitesSent.map(esc).join("</b>, <b>")}</b>. Each teammate picks their own password from the email, then signs in to the operator portal.</span></div>` : ""}
    ${state.inviteWarning ? `<div class="notice"><i data-lucide="info"></i><span>${esc(state.inviteWarning)}</span></div>` : ""}
    ${state.formError ? `<div class="notice"><i data-lucide="shield-alert"></i><span>${esc(state.formError)} You can create one in Admin.</span></div>` : ""}
    <div class="keyrow"><span class="k">Tenant</span><span class="v">${esc(state.resultSlug)}</span><span class="pf-chip" style="padding:4px 10px;font-size:11px">${esc(state.plan)} profile</span></div>
    <div class="keyrow"><span class="k">API key</span><span class="v" id="apikey">${esc(maskedKey())}</span><button class="minibtn" id="revealKey" type="button">Reveal</button><button class="minibtn" id="copyKey" type="button">Copy</button></div>
    <div class="snip">
      <button class="pf-copy copy" id="copySnip" type="button">Copy</button>
      <pre id="snipPre"></pre>
    </div>
    <div class="keyrow" style="align-items:flex-start">
      <span class="k" style="padding-top:3px">Agent</span>
      <span class="v" style="font-family:var(--uc-font-body);font-weight:400;color:var(--uc-fg2);white-space:normal">Building with Claude Code, Cursor, or another coding agent? Copy a ready-made setup prompt — it carries your tenant ID, endpoints, and a link to the machine-readable contract.</span>
      <button class="minibtn" id="copyAgentPrompt" type="button" style="white-space:nowrap"><i data-lucide="sparkles" style="width:12px;height:12px"></i>&nbsp;Copy agent prompt</button>
    </div>
    <div class="nextgrid">
      ${
        brand.consoleUrl
          ? `<a class="nextcard" href="${esc(brand.consoleUrl)}"><span class="ic"><i data-lucide="headset"></i></span><span><b>Open operator portal</b><span>Answer live chats</span></span></a>`
          : `<a class="nextcard" href="${esc(brand.adminUrl)}#team"><span class="ic"><i data-lucide="headset"></i></span><span><b>Invite operators</b><span>Portal URL not configured</span></span></a>`
      }
      <a class="nextcard" href="${esc(brand.adminUrl)}"><span class="ic"><i data-lucide="sliders-horizontal"></i></span><span><b>Continue to Admin</b><span>${state.adminSessionReady ? "Already signed in" : "Manage workspace"}</span></span></a>
      <a class="nextcard" href="docs.html#quickstart"><span class="ic"><i data-lucide="book-open"></i></span><span><b>Read the quickstart</b><span>Wire up auth</span></span></a>
      <a class="nextcard" href="docs.html#api"><span class="ic"><i data-lucide="terminal"></i></span><span><b>API reference</b><span>Build on the API</span></span></a>
    </div>`,

  // 5th terminal view — prod path where the user must confirm via emailed link.
  email: () => `
    <div class="done-ic warn"><i data-lucide="mail"></i></div>
    <h1>Check your email to finish.</h1>
    <p class="sub">We sent a confirmation link to <b style="color:var(--uc-fg1)">${esc(state.email)}</b>. Click it to verify your account and activate <b style="color:var(--uc-fg1)">${esc(state.workspace || state.resultSlug)}</b>.</p>
    <div class="notice"><i data-lucide="info"></i><span>The link is good for ${esc(state.expiresIn || "24 hours")}${state.invites.some((iv) => iv.email.trim()) ? " — your teammate invites go out as soon as you confirm (keep using this browser)" : ""}. Nothing yet? Check spam, then send it again below.</span></div>
    ${state.resendNotice ? `<div class="${state.resendError ? "formerr" : "formok"}" role="status"><i data-lucide="${state.resendError ? "shield-alert" : "mail-check"}"></i><span>${esc(state.resendNotice)}</span></div>` : ""}
    <div class="nav"><a class="pf-btn pf-btn--ghost pf-btn--lg" href="landing.html">Back to home</a><div class="grow"></div><button class="pf-btn pf-btn--primary pf-btn--lg" id="resendBtn" type="button"><i data-lucide="send"></i>Send the email again</button></div>`,

  // verify-link landing while we exchange the token / issue a key
  verifying: () => `
    <div class="done-ic"><span class="btnspin" style="width:26px;height:26px;color:var(--uc-teal-700)"></span></div>
    <h1>Activating your workspace…</h1>
    <p class="sub">Confirming your account and minting your first API key. This only takes a moment.</p>`,

  // verify-link landing failure (expired / invalid token)
  verifyFailed: () => `
    <div class="done-ic warn"><i data-lucide="shield-alert"></i></div>
    <h1>This link can't be used.</h1>
    <p class="sub">${esc(state.formError || "We couldn't confirm this link. Start over to get a new one.")}</p>
    <div class="nav"><a class="pf-btn pf-btn--ghost pf-btn--lg" href="${esc(brand.adminUrl)}"><i data-lucide="log-in"></i>Sign in</a><div class="grow"></div><a class="pf-btn pf-btn--primary pf-btn--lg" href="signup.html">Start over<i data-lucide="arrow-right"></i></a></div>`,
};

// The verify link's failure codes, in sentences (never the raw API message).
function mapVerifyError(e) {
  const code = (e && e.code ? String(e.code) : "").toUpperCase();
  if (code === "NOT_FOUND") return "This confirmation link isn't valid — it may have already been used. Sign in if your workspace exists, or start over.";
  if (code === "EXPIRED") return "This link has expired. Start over and we'll send a fresh one.";
  if (code === "NETWORK_ERROR") return e.message;
  return "We couldn't confirm this link. Start over to get a new one.";
}

function formErrorHTML() {
  if (!state.formError) return "";
  return `<div class="formerr" role="alert"><i data-lucide="shield-alert"></i><span>${esc(state.formError)}</span></div>`;
}

function maskedKey() {
  if (!state.apiSecret) return "—";
  const head = state.keyPrefix || state.apiSecret.slice(0, 8);
  return `${head}${"•".repeat(20)}`;
}

// Build a self-contained setup prompt a developer can paste into their coding
// agent. It must work cold: tenant id, endpoints, and the llms.txt contract.
function buildAgentPrompt() {
  const tenant = state.resultTenantID || "<your-tenant-id>";
  const origin = window.location.origin;
  return [
    `Install Iris chat (${origin}) in this app.`,
    ``,
    `Workspace details:`,
    `- Tenant ID: ${tenant}`,
    `- API base: ${brand.apiBase}`,
    `- Widget script: ${brand.cdnBase}/widget.js`,
    ``,
    `Steps:`,
    `1. Read ${origin}/llms.txt first — it is the complete machine-readable contract (auth, REST + product API, widget embed, examples).`,
    `2. Add the widget embed snippet before </body> on the pages that should offer chat:`,
    `   <script src="${brand.cdnBase}/widget.js" data-api-base="${brand.apiBase}" data-tenant-id="${tenant}" data-mode="customer" defer></script>`,
    `3. Add every origin the widget loads on to Allowed Origins (Iris Admin console -> Widget). Ask the workspace owner if you don't have admin access.`,
    `4. Optional, server-to-server: get a secret API key (iris_sk_*, Admin console -> API keys; shown once), store it in the secret manager, and verify it:`,
    `   curl ${brand.apiBase}/api/product/v1/me -H "Authorization: Bearer $IRIS_API_KEY"`,
    `5. Optional, signed-in app users: mint chat sessions from your backend via POST ${brand.apiBase}/api/product/v1/sessions with {"external_user_id": "<your stable user id>"}, then pass the returned token to the widget as data-token (and chat.channel_id as data-initial-channel-id).`,
    ``,
    `Docs for humans: ${origin}/docs.html`,
  ].join("\n");
}

// Build the real embed snippet from the live tenant id. The first API key is
// shown separately because product API-key request auth is not mounted yet.
function buildSnippet() {
  const tenant = state.resultTenantID || state.resultSlug || "your-tenant-id";
  return `<span class="tok-com">&lt;!-- Add before &lt;/body&gt; --&gt;</span>
<span class="tok-tag">&lt;script</span> <span class="tok-key">src</span>=<span class="tok-str">"${esc(brand.cdnBase)}/widget.js"</span>
        <span class="tok-key">data-api-base</span>=<span class="tok-str">"${esc(apiBase)}"</span>
        <span class="tok-key">data-tenant-id</span>=<span class="tok-str">"${esc(tenant)}"</span>
        <span class="tok-key">data-mode</span>=<span class="tok-str">"customer"</span><span class="tok-tag">&gt;&lt;/script&gt;</span>`;
}

// ---- render + wiring -------------------------------------------------------

function render() {
  const view = views[state.step] || views[0];
  panel.innerHTML = view();
  // Hide the "sign in" topbar on terminal screens.
  const terminal = ["email", "verifying", "verifyFailed"].includes(state.step);
  topbar.style.visibility =
    state.step === 3 || terminal ? "hidden" : "visible";
  if (typeof state.step === "number") renderStepper();

  const next = $("next");
  const back = $("back");
  if (back)
    back.onclick = () => {
      state.formError = "";
      state.step--;
      render();
    };

  if (state.step === 0) wireStep0(next);
  else if (state.step === 1) wireStep1(next);
  else if (state.step === 2) wireStep2(next);
  else if (state.step === 3) wireStep3();
  else if (state.step === "email") wireEmailStep();

  mountIcons();
}

// "Send the email again": rotates the pending signup's link server-side. The
// API answers 202 either way, so the notice never reveals whether the address
// has a signup — the visitor typed it moments ago, so we just confirm.
function wireEmailStep() {
  const btn = $("resendBtn");
  if (!btn) return;
  btn.onclick = async () => {
    if (state.busy) return;
    setBusy(btn, true, "Sending…");
    try {
      const res = await resendSignup({ email: state.email.trim() });
      state.resendError = false;
      state.resendNotice = `Sent again to ${state.email.trim()}.`;
      if (res && res.expires_at) state.expiresIn = humanExpiry(res.expires_at) || state.expiresIn;
    } catch (e) {
      state.resendError = true;
      state.resendNotice = e.message || "Couldn't resend right now. Try again in a minute.";
    } finally {
      state.busy = false;
    }
    render();
  };
}

function wireStep0(next) {
  const name = $("f_name");
  const email = $("f_email");
  const pass = $("f_pass");
  // keep state in sync so values survive re-renders
  name.oninput = () => (state.name = name.value);
  email.oninput = () => {
    state.email = email.value;
    $("emailErr").textContent = "";
  };
  pass.oninput = () => {
    state.password = pass.value;
    $("passErr").textContent = "";
  };
  next.onclick = () => {
    state.name = name.value;
    state.email = email.value;
    state.password = pass.value;
    let ok = true;
    if (!isEmail(state.email)) {
      $("emailErr").textContent = "Enter a valid work email.";
      email.focus();
      ok = false;
    }
    if (state.password.length < MIN_PASSWORD) {
      $("passErr").textContent = `Use at least ${MIN_PASSWORD} characters.`;
      if (ok) pass.focus();
      ok = false;
    }
    if (!ok) return;
    state.formError = "";
    state.step = 1;
    render();
  };
}

let slugTimer = null;
let slugSeq = 0; // guard against out-of-order debounced responses

function wireStep1(next) {
  const ws = $("f_ws");
  const slug = $("f_slug");
  const wrap = $("slugWrap");
  const hint = $("slugHint");
  let slugEdited = !!state.slug;

  const paintHint = () => {
    wrap.classList.toggle(
      "is-bad",
      state.slugStatus === "taken" || state.slugStatus === "invalid",
    );
    const map = {
      idle: { cls: "hint--muted", html: "Letters, numbers and hyphens." },
      checking: {
        cls: "hint--muted",
        html: '<span class="spin"></span>Checking availability…',
      },
      available: {
        cls: "hint--ok",
        html: `<i data-lucide="check"></i>${esc(slug.value)}.${esc(brand.domain)} is available.`,
      },
      taken: {
        cls: "hint--bad",
        html: `<i data-lucide="shield-alert"></i>That URL is taken. Try another.`,
      },
      invalid: {
        cls: "hint--bad",
        html: `<i data-lucide="shield-alert"></i>Use 3–28 letters, numbers or hyphens.`,
      },
    };
    const h = map[state.slugStatus] || map.idle;
    hint.className = `hint ${h.cls}`;
    hint.innerHTML = h.html;
    mountIcons();
  };

  const checkSlug = () => {
    const value = slug.value.trim();
    state.slug = value;
    if (!value) {
      state.slugStatus = "idle";
      paintHint();
      return;
    }
    if (!slugLooksValid(value)) {
      state.slugStatus = "invalid";
      paintHint();
      return;
    }
    state.slugStatus = "checking";
    paintHint();
    const seq = ++slugSeq;
    clearTimeout(slugTimer);
    slugTimer = setTimeout(async () => {
      try {
        const res = await slugAvailable(value);
        if (seq !== slugSeq) return; // a newer keystroke superseded this
        state.slugStatus = res && res.available ? "available" : "taken";
      } catch {
        if (seq !== slugSeq) return;
        // Don't hard-block on a flaky availability check; the POST is final.
        state.slugStatus = "idle";
      }
      paintHint();
    }, SLUG_DEBOUNCE_MS);
  };

  ws.oninput = () => {
    state.workspace = ws.value;
    if (!slugEdited) {
      slug.value = slugify(ws.value);
      checkSlug();
    }
  };
  slug.oninput = () => {
    slugEdited = true;
    checkSlug();
  };
  paintHint();
  if (state.slug) checkSlug();

  next.onclick = () => {
    state.workspace = ws.value.trim() || slug.value.trim();
    state.slug = slug.value.trim();
    if (!state.workspace) {
      ws.focus();
      return;
    }
    if (!slugLooksValid(state.slug)) {
      state.slugStatus = "invalid";
      paintHint();
      slug.focus();
      return;
    }
    if (state.slugStatus === "taken") {
      slug.focus();
      return;
    }
    state.formError = "";
    state.step = 2;
    render();
  };
}

function wireStep2(next) {
  renderInvites();
  $("addInvite").onclick = () => {
    state.invites.push({ email: "", role: "Operator" });
    renderInvites();
    mountIcons();
  };
  $("plans")
    .querySelectorAll("[data-plan]")
    .forEach((el) => {
      el.onclick = () => {
        state.plan = el.dataset.plan;
        document
          .querySelectorAll(".plan")
          .forEach((p) => p.classList.toggle("on", p === el));
      };
    });
  next.onclick = () => submitWorkspace(next);
}

function renderInvites() {
  const wrap = $("invites");
  if (!wrap) return;
  wrap.innerHTML = state.invites
    .map(
      (iv, idx) => `
    <div class="invite">
      <input class="inp" data-iv="${idx}" type="email" placeholder="teammate@company.com" value="${esc(iv.email)}" autocomplete="off">
      <select class="sel" data-ivr="${idx}" aria-label="Role">
        ${ROLES.map((r) => `<option ${r === iv.role ? "selected" : ""}>${r}</option>`).join("")}
      </select>
    </div>`,
    )
    .join("");
  wrap
    .querySelectorAll("[data-iv]")
    .forEach(
      (el) =>
        (el.oninput = (e) => (state.invites[+el.dataset.iv].email = e.target.value)),
    );
  wrap
    .querySelectorAll("[data-ivr]")
    .forEach(
      (el) =>
        (el.onchange = (e) =>
          (state.invites[+el.dataset.ivr].role = e.target.value)),
    );
}

function setBusy(btn, busy, label) {
  state.busy = busy;
  if (!btn) return;
  btn.disabled = busy;
  if (busy) {
    btn.innerHTML = `<span class="btnspin"></span>${esc(label || "Working…")}`;
  }
}

function persistAdminSession(session) {
  const token = session && session.token;
  if (!token) return false;
  try {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    if (state.email) sessionStorage.setItem(ADMIN_EMAIL_KEY, state.email.trim());
    return true;
  } catch {
    return false;
  }
}

// The tricky bit: ordered async sequence behind one button press.
async function submitWorkspace(btn) {
  if (state.busy) return;
  state.formError = "";
  setBusy(btn, true, "Creating workspace…");
  try {
    // 1) Create the signup (tenant + owner, pending verification).
    let res;
    try {
      res = await signup({
        fullName: state.name,
        email: state.email,
        password: state.password,
        companyName: state.workspace,
        slug: state.slug,
        plan: state.plan,
      });
    } catch (e) {
      return handleSignupError(e);
    }

    state.resultSlug = res.slug || state.slug;
    state.resultTenantID = res.tenant_id || "";
    state.expiresIn = humanExpiry(res.expires_at);

    // 2a) Prod: no token returned -> tell the user to confirm by email. STOP.
    if (!res.verification_token) {
      stashPendingSetup(res.tenant_id);
      state.emailPending = true;
      state.step = "email";
      return render();
    }

    // 2b) Dev: exchange the token for a verified session immediately.
    let session;
    try {
      session = await verifySignup({
        tenantId: res.tenant_id,
        token: res.verification_token,
      });
    } catch (e) {
      state.formError = `Verification failed: ${e.message}`;
      return render();
    }
    state.session = session;
    state.adminSessionReady = persistAdminSession(session);
    const bearer = session.token;

    // 3) Best-effort invites — collect failures, never abort the whole flow.
    await sendInvites(bearer);

    // 4) Mint the first publishable key (secret shown once).
    try {
      const key = await createKey({
        token: bearer,
        name: "Default publishable key",
        keyType: "publishable",
      });
      state.apiSecret = key.secret || "";
      state.keyPrefix = key.key_prefix || "";
    } catch (e) {
      state.formError = `Workspace created, but we couldn't mint an API key: ${e.message}`;
    }

    // 5) Success.
    state.step = 3;
    render();
  } finally {
    state.busy = false;
  }
}

// Map server errors to the right recovery step.
function handleSignupError(e) {
  const code = (e.code || "").toUpperCase();
  if (code === "CONFLICT" || code.includes("SLUG")) {
    state.slugStatus = "taken";
    state.formError = "That workspace URL is already taken — pick another.";
    state.step = 1;
    return render();
  }
  if (code === "WEAK_PASSWORD" || code.includes("PASSWORD") || code.includes("EMAIL")) {
    state.formError = e.message;
    state.step = 0;
    return render();
  }
  state.formError = e.message || "Something went wrong creating your workspace.";
  render();
}

// Send each non-empty invite; tolerate per-invite failures.
async function sendInvites(bearer) {
  const pending = state.invites.filter((iv) => iv.email.trim());
  const sent = [];
  let failed = 0;
  for (const iv of pending) {
    try {
      await createInvite({
        token: bearer,
        email: iv.email.trim(),
        orgRole: iv.role.toLowerCase(),
      });
      sent.push(iv.email.trim());
    } catch {
      failed++;
    }
  }
  state.invitesSent = sent;
  if (failed > 0) {
    state.inviteWarning = `${failed} of ${pending.length} invites couldn't be sent — you can re-invite from Admin → Team.`;
  }
}

function wireStep3() {
  $("snipPre").innerHTML = buildSnippet();

  $("revealKey").onclick = (e) => {
    const cell = $("apikey");
    const revealed = e.target.dataset.shown === "1";
    if (revealed) {
      cell.textContent = maskedKey();
      e.target.textContent = "Reveal";
      e.target.dataset.shown = "0";
    } else {
      cell.textContent = state.apiSecret || "—";
      e.target.textContent = "Hide";
      e.target.dataset.shown = "1";
    }
  };

  $("copyKey").onclick = (e) => copyText(e.target, state.apiSecret, "Copy");
  $("copySnip").onclick = (e) =>
    copyText(e.target, $("snipPre").textContent, "Copy");
  const agentBtn = $("copyAgentPrompt");
  if (agentBtn)
    agentBtn.onclick = () => {
      if (navigator.clipboard) navigator.clipboard.writeText(buildAgentPrompt());
      agentBtn.textContent = "Copied — paste it into your agent";
      setTimeout(() => {
        agentBtn.innerHTML = `<i data-lucide="sparkles" style="width:12px;height:12px"></i>&nbsp;Copy agent prompt`;
        mountIcons();
      }, 2200);
    };
}

function copyText(btn, text, restore) {
  if (text && navigator.clipboard) navigator.clipboard.writeText(text);
  const prev = btn.textContent;
  btn.textContent = "Copied";
  setTimeout(() => (btn.textContent = restore || prev), 1400);
}

// ---- verify-link landing ---------------------------------------------------
// The emailed confirmation link hits signup.html?tenant_id=..&token=.. — skip
// the stepper, verify, mint a key, and render success. On failure, show why.
async function runVerifyLink(tenantId, token, slug) {
  state.step = "verifying";
  state.resultTenantID = tenantId;
  render();
  let session;
  try {
    session = await verifySignup({ tenantId, token });
  } catch (e) {
    state.step = "verifyFailed";
    state.formError = mapVerifyError(e);
    return render();
  }
  state.session = session;
  state.adminSessionReady = persistAdminSession(session);
  const sess = session.session || {};
  // The verification link carries the workspace slug (the session payload only
  // has the tenant UUID), so prefer it for the displayed handle + embed snippet.
  state.resultSlug = slug || sess.slug || tenantId;
  if (slug) state.workspace = slug;
  const bearer = session.token;

  // Teammates entered before the email hop live in this browser only; send
  // them now (best-effort) so nobody is silently dropped.
  restorePendingSetup(tenantId);
  await sendInvites(bearer);
  clearPendingSetup();

  try {
    const key = await createKey({
      token: bearer,
      name: "Default publishable key",
      keyType: "publishable",
    });
    state.apiSecret = key.secret || "";
    state.keyPrefix = key.key_prefix || "";
  } catch (e) {
    state.formError = `Verified, but we couldn't mint an API key: ${e.message}`;
  }
  state.step = 3;
  render();
}

function stashPendingSetup(tenantId) {
  const invites = state.invites.filter((iv) => iv.email.trim());
  if (!tenantId) return;
  try {
    localStorage.setItem(
      PENDING_SETUP_KEY,
      JSON.stringify({ tenantId, invites, plan: state.plan, workspace: state.workspace, savedAt: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode): the success screen still says how to invite from Admin.
  }
}

function restorePendingSetup(tenantId) {
  try {
    const raw = localStorage.getItem(PENDING_SETUP_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (!saved || saved.tenantId !== tenantId) return;
    state.invites = Array.isArray(saved.invites) ? saved.invites : [];
    if (saved.plan) state.plan = saved.plan;
    if (saved.workspace) state.workspace = saved.workspace;
  } catch {
    // Corrupt or unavailable storage: nothing to restore.
  }
}

function clearPendingSetup() {
  try {
    localStorage.removeItem(PENDING_SETUP_KEY);
  } catch {
    // ignore
  }
}

// ---- boot ------------------------------------------------------------------

applyBrand();
const params = new URLSearchParams(location.search);
const linkTenant = params.get("tenant_id");
const linkToken = params.get("token");
const linkSlug = params.get("slug");
if (linkTenant && linkToken) {
  // Scrub the one-time verification token from the URL/history before we do
  // anything else — it should never linger in browser history or get synced.
  history.replaceState(null, "", location.pathname);
  runVerifyLink(linkTenant, linkToken, linkSlug);
} else {
  render();
}
